import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, Play, Pause } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { JOB_ROLE_CATEGORIES, getAllJobRoles } from '@/types/jobRoles';
import { toast } from 'sonner';

interface GenerationStatus {
  roleId: string;
  roleLabel: string;
  status: 'pending' | 'generating' | 'completed' | 'error';
  currentCount: number;
  targetCount: number;
  generated: number;
  error?: string;
}

export const QuestionGenerationPanel: React.FC = () => {
  const [generationStatuses, setGenerationStatuses] = useState<GenerationStatus[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentRole, setCurrentRole] = useState<string>('');
  const [overallProgress, setOverallProgress] = useState(0);

  useEffect(() => {
    loadRoleStatuses();
  }, []);

  const loadRoleStatuses = async () => {
    // Get all roles
    const allRoles = getAllJobRoles();
    
    // Get current question counts
    const { data: questionCounts } = await supabase
      .from('interview_questions')
      .select('role_type');

    const countsMap: Record<string, number> = {};
    questionCounts?.forEach((item) => {
      countsMap[item.role_type] = (countsMap[item.role_type] || 0) + 1;
    });

    // Create generation status for each role
    const statuses: GenerationStatus[] = allRoles.map(role => {
      const currentCount = countsMap[role.id] || 0;
      let targetCount = 150;
      
      // Adjust target based on role category
      if (role.category === 'development') targetCount = 200;
      if (role.category === 'data-analytics') targetCount = 180;
      if (role.category === 'management') targetCount = 150;
      if (role.category === 'product-program') targetCount = 150;
      
      return {
        roleId: role.id,
        roleLabel: role.label,
        status: currentCount >= targetCount ? 'completed' : 'pending',
        currentCount,
        targetCount,
        generated: 0
      };
    });

    // Sort: roles with 0 questions first, then by count ascending
    statuses.sort((a, b) => {
      if (a.currentCount === 0 && b.currentCount > 0) return -1;
      if (a.currentCount > 0 && b.currentCount === 0) return 1;
      return a.currentCount - b.currentCount;
    });

    setGenerationStatuses(statuses);
  };

  const generateQuestionsForRole = async (
    roleId: string,
    roleLabel: string,
    neededCount: number,
    difficulty: string = 'mid'
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke('bulk-generate-role-questions', {
        body: {
          roleId,
          roleLabel,
          difficulty,
          count: Math.min(neededCount, 50), // Generate in batches of 50
          batchSize: 10
        }
      });

      if (error) throw error;
      return data?.count || 0;
    } catch (error: any) {
      console.error(`Error generating for ${roleLabel}:`, error);
      throw error;
    }
  };

  const startBulkGeneration = async () => {
    setIsGenerating(true);
    
    const rolesToGenerate = generationStatuses.filter(
      s => s.status === 'pending' && s.currentCount < s.targetCount
    );

    let completed = 0;
    
    for (const role of rolesToGenerate) {
      setCurrentRole(role.roleLabel);
      
      // Update status to generating
      setGenerationStatuses(prev => 
        prev.map(s => s.roleId === role.roleId ? { ...s, status: 'generating' } : s)
      );

      try {
        const neededCount = role.targetCount - role.currentCount;
        const difficulties = ['entry', 'mid', 'senior', 'staff', 'principal'];
        const questionsPerDifficulty = Math.ceil(neededCount / difficulties.length);

        let totalGenerated = 0;

        // Generate for each difficulty level
        for (const difficulty of difficulties) {
          if (totalGenerated >= neededCount) break;
          
          const countToGenerate = Math.min(questionsPerDifficulty, neededCount - totalGenerated);
          const batchCount = Math.ceil(countToGenerate / 50);
          
          for (let batch = 0; batch < batchCount; batch++) {
            const batchSize = Math.min(50, countToGenerate - (batch * 50));
            const generated = await generateQuestionsForRole(
              role.roleId,
              role.roleLabel,
              batchSize,
              difficulty
            );
            
            totalGenerated += generated;
            
            // Update progress
            setGenerationStatuses(prev => 
              prev.map(s => s.roleId === role.roleId 
                ? { ...s, generated: totalGenerated, currentCount: s.currentCount + generated }
                : s
              )
            );

            // Small delay to avoid rate limits
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }

        // Mark as completed
        setGenerationStatuses(prev => 
          prev.map(s => s.roleId === role.roleId 
            ? { ...s, status: 'completed' }
            : s
          )
        );

        completed++;
        setOverallProgress((completed / rolesToGenerate.length) * 100);
        
        toast.success(`Generated ${totalGenerated} questions for ${role.roleLabel}`);
        
      } catch (error: any) {
        console.error(`Error generating for ${role.roleLabel}:`, error);
        setGenerationStatuses(prev => 
          prev.map(s => s.roleId === role.roleId 
            ? { ...s, status: 'error', error: error.message }
            : s
          )
        );
        toast.error(`Failed to generate for ${role.roleLabel}: ${error.message}`);
      }
    }

    setIsGenerating(false);
    setCurrentRole('');
    toast.success('Bulk generation completed!');
  };

  const generateForSingleRole = async (roleId: string, roleLabel: string, neededCount: number) => {
    setGenerationStatuses(prev => 
      prev.map(s => s.roleId === roleId ? { ...s, status: 'generating' } : s)
    );

    try {
      const generated = await generateQuestionsForRole(roleId, roleLabel, Math.min(neededCount, 50));
      
      setGenerationStatuses(prev => 
        prev.map(s => s.roleId === roleId 
          ? { ...s, status: 'completed', generated, currentCount: s.currentCount + generated }
          : s
        )
      );
      
      toast.success(`Generated ${generated} questions for ${roleLabel}`);
    } catch (error: any) {
      setGenerationStatuses(prev => 
        prev.map(s => s.roleId === roleId 
          ? { ...s, status: 'error', error: error.message }
          : s
        )
      );
      toast.error(`Failed: ${error.message}`);
    }
  };

  const pendingCount = generationStatuses.filter(s => s.status === 'pending' && s.currentCount < s.targetCount).length;
  const completedCount = generationStatuses.filter(s => s.status === 'completed').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Question Generation Management</CardTitle>
        <CardDescription>
          Generate world-class interview questions for all roles
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{completedCount}</div>
              <div className="text-sm text-muted-foreground">Completed Roles</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{pendingCount}</div>
              <div className="text-sm text-muted-foreground">Pending Roles</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">
                {generationStatuses.reduce((sum, s) => sum + s.currentCount, 0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Questions</div>
            </CardContent>
          </Card>
        </div>

        {/* Bulk Actions */}
        <div className="flex gap-4">
          <Button
            onClick={startBulkGeneration}
            disabled={isGenerating || pendingCount === 0}
            size="lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Start Bulk Generation ({pendingCount} roles)
              </>
            )}
          </Button>
          
          <Button
            onClick={loadRoleStatuses}
            variant="outline"
            disabled={isGenerating}
          >
            Refresh Status
          </Button>
        </div>

        {isGenerating && (
          <Alert>
            <Loader2 className="h-4 w-4 animate-spin" />
            <AlertDescription>
              Currently generating for: <strong>{currentRole}</strong>
              <Progress value={overallProgress} className="mt-2" />
            </AlertDescription>
          </Alert>
        )}

        {/* Role Status List */}
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {JOB_ROLE_CATEGORIES.map(category => {
            const categoryRoles = generationStatuses.filter(
              s => category.roles.some(r => r.id === s.roleId)
            );

            return (
              <div key={category.id} className="space-y-2">
                <h3 className="font-semibold text-sm flex items-center gap-2 mt-4">
                  <span>{category.icon}</span>
                  <span>{category.name}</span>
                </h3>
                
                {categoryRoles.map(status => (
                  <Card key={status.roleId}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">{status.roleLabel}</span>
                            {status.status === 'completed' && (
                              <Badge variant="default" className="bg-green-500">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Complete
                              </Badge>
                            )}
                            {status.status === 'generating' && (
                              <Badge variant="default">
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Generating
                              </Badge>
                            )}
                            {status.status === 'error' && (
                              <Badge variant="destructive">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Error
                              </Badge>
                            )}
                          </div>
                          
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">
                                Current: {status.currentCount} / Target: {status.targetCount}
                              </span>
                              {status.generated > 0 && (
                                <span className="text-green-600">+{status.generated} generated</span>
                              )}
                            </div>
                            <Progress 
                              value={(status.currentCount / status.targetCount) * 100} 
                              className="h-2"
                            />
                          </div>
                          
                          {status.error && (
                            <p className="text-sm text-red-600 mt-2">{status.error}</p>
                          )}
                        </div>
                        
                        <div className="ml-4">
                          {status.currentCount < status.targetCount && status.status !== 'generating' && (
                            <Button
                              onClick={() => generateForSingleRole(
                                status.roleId,
                                status.roleLabel,
                                status.targetCount - status.currentCount
                              )}
                              size="sm"
                              disabled={isGenerating}
                            >
                              Generate +50
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
